import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { FilingsResponse } from '@/finsight/domain/vo/Filings/Filings';
import { useDIContext } from '@/Framework/DI/DIContext';
import FilingRepository from '@/finsight/infrastructure/repository/FilingRepository';
import useFetchCollection from '@/Framework/State/useFetchCollection';
import { FILING_ALIASES } from '@/finsight/ui/components/filings/constants';

const useFilings = (initialFilings) => {
  const { container } = useDIContext();
  const { collection = [], totalCount = 0, hasServerError = false, xml2csvUrlTpl } = initialFilings;
  const [isFirstFetching, setIsFirstFetching] = useState<boolean>(true);
  const [isNewFilings, setIsNewFilings] = useState<boolean>(false);
  const filingRepository = container.get(FilingRepository);

  const fetchFiling = useCallback(
      async (payload) => {
        const response = await filingRepository.getFilingsLoanLevel(payload);
        return ({
          totalCount: response?.totalCount,
          collection: response?.collection,
        });
      },
      [],
  );

  const filingListing = useFetchCollection(
    (isFirstFetching && !hasServerError) ? null : fetchFiling,
    { isNewFilings }, {
      collection,
      totalCount,
    });

  const handleNewFilingsChange = (key) => {
    if (!isFirstFetching) {
      filingListing.paginate(1);
    }
    setIsNewFilings(key);
  };

  useEffect(() => () => {
    setIsFirstFetching(false);
  }, [
    isNewFilings,
    filingListing.page,
    filingListing.page,
    filingListing.sortBy,
    filingListing.sortOrder,
    filingListing.perPage,
    filingListing.debouncedSearchString,
  ]);

  const handleSearchChange = (value: string) => {
    const newSearchString = FILING_ALIASES[value.toLowerCase()] || value;
    filingListing.debouncedSearch(newSearchString);
  };

  return {
    ...filingListing,
    isNewFilings,
    setIsNewFilings,
    xml2csvUrlTpl,
    handleSearchChange,
    handleNewFilingsChange,
  };
};

type FilingsContextType = ReturnType<typeof useFilings>;

export const FilingsContext = createContext<FilingsContextType>(null);

export const useFilingContext = () => {
  const context = useContext(FilingsContext);
  if (!context) {
    throw new Error('useFilingContext must be used within the FilingsContext');
  }
  return context;
};

interface IProps {
  filingsData?: FilingsResponse,
  children: React.ReactNode,
}

const FilingsContextProvider = ({ children, filingsData }: IProps) => (
  <FilingsContext.Provider value={ useFilings(filingsData) }>
    { children }
  </FilingsContext.Provider>
);

export default FilingsContextProvider;
